import React, { useState, useEffect, useCallback } from 'react';
import { BackofficeLayout } from '../../layouts/BackofficeLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Search, Plus, ChevronRight, User, Trash2, ShieldCheck, ShieldAlert, Mail, AlertTriangle } from 'lucide-react';
import { api } from '../../context/AuthContext';
import { toast } from 'sonner';

const STATUS_COLORS = {
  ready: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  review: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  new: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  researched: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
  contacted: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  replied: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  booked: 'bg-sky-500/20 text-sky-400 border-sky-500/30',
  qualified: 'bg-green-500/20 text-green-400 border-green-500/30',
  won: 'bg-green-600/20 text-green-300 border-green-600/30',
  lost: 'bg-red-500/20 text-red-400 border-red-500/30',
  do_not_contact: 'bg-red-700/20 text-red-300 border-red-700/30',
};

const CONFIDENCE_ICONS = {
  high: <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />,
  medium: <ShieldAlert className="w-3.5 h-3.5 text-amber-400" />,
  low: <AlertTriangle className="w-3.5 h-3.5 text-red-400" />,
};

const FILTERS = [
  { label: 'Ready', value: 'ready' },
  { label: 'Review', value: 'review' },
  { label: 'All', value: '' },
  { label: 'Contacted', value: 'contacted' },
  { label: 'Replied', value: 'replied' },
];

const EMPTY = { company_name: '', email: '', contact_name: '', contact_role: '', phone: '', region: '', source: 'manual', status: 'new', score: 50, tags: '', notes: '' };

export default function BackofficeProspects() {
  const [prospects, setProspects] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ready');
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [selected, setSelected] = useState(null);
  const [activities, setActivities] = useState([]);

  const load = useCallback((q = '', status = statusFilter) => {
    setLoading(true);
    const params = { limit: 100 };
    if (q) params.search = q;
    if (status) params.status = status;
    api.get('/backoffice/prospects', { params })
      .then(r => { setProspects(r.data.prospects); setTotal(r.data.total); })
      .catch(() => toast.error('Failed to load prospects'))
      .finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => { load('', statusFilter); }, [statusFilter, load]);

  const handleCreate = async () => {
    try {
      const payload = { ...form, score: Number(form.score), tags: form.tags ? form.tags.split(',').map(t => t.trim()) : [] };
      await api.post('/backoffice/prospects', payload);
      toast.success('Prospect created');
      setShowCreate(false);
      setForm(EMPTY);
      load('', statusFilter);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this prospect and all associated data?')) return;
    try {
      await api.delete(`/backoffice/prospects/${id}`);
      toast.success('Prospect deleted');
      setSelected(null);
      load('', statusFilter);
    } catch { toast.error('Failed to delete'); }
  };

  const openDetail = async (p) => {
    setSelected(p);
    try {
      const r = await api.get(`/backoffice/prospects/${p.id}/activities`);
      setActivities(r.data.activities || []);
    } catch { setActivities([]); }
  };

  return (
    <BackofficeLayout>
      <div data-testid="backoffice-prospects">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
            PROSPECTS <span className="text-slate-500 font-mono text-sm ml-2">{total}</span>
          </h1>
          <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm" onClick={() => setShowCreate(true)} data-testid="create-prospect-btn">
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 mb-3">
          {FILTERS.map(f => (
            <button
              key={f.value}
              onClick={() => setStatusFilter(f.value)}
              className={`px-3 py-1 text-xs rounded-sm transition-colors ${
                statusFilter === f.value
                  ? 'bg-orange-500/20 text-orange-400 border border-orange-500/40'
                  : 'text-slate-500 hover:text-slate-300 border border-slate-800'
              }`}
              data-testid={`filter-${f.value || 'all'}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-500" />
          <Input
            placeholder="Search company, name or email..."
            className="pl-9 bg-slate-900 border-slate-700 text-slate-200 h-9 rounded-sm"
            value={search}
            onChange={e => { setSearch(e.target.value); load(e.target.value, statusFilter); }}
            data-testid="prospect-search"
          />
        </div>

        {/* List */}
        <div className="space-y-1">
          {loading ? (
            [...Array(5)].map((_, i) => <div key={i} className="h-14 bg-slate-900 border border-slate-800 rounded-sm animate-pulse" />)
          ) : prospects.length === 0 ? (
            <p className="text-slate-500 text-sm py-8 text-center">
              {statusFilter ? `No ${statusFilter} prospects` : 'No prospects found'}
            </p>
          ) : (
            prospects.map(p => (
              <button
                key={p.id}
                onClick={() => openDetail(p)}
                className="w-full flex items-center justify-between px-4 py-3 bg-slate-900 border border-slate-800 rounded-sm hover:border-slate-600 transition-colors text-left"
                data-testid={`prospect-row-${p.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-sm bg-slate-800 flex items-center justify-center flex-shrink-0">
                    {p.contact_confidence ? CONFIDENCE_ICONS[p.contact_confidence] || <User className="w-4 h-4 text-slate-500" strokeWidth={1.5} /> : <User className="w-4 h-4 text-slate-500" strokeWidth={1.5} />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-200 truncate">{p.company_name}</p>
                    <p className="text-xs text-slate-500 truncate">
                      {p.contact_name || '—'} {p.contact_role ? `· ${p.contact_role}` : ''} {p.email ? `· ${p.email}` : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {p.search_category && <span className="text-[9px] text-slate-600 uppercase">{p.search_category}</span>}
                  {p.source && p.source !== 'manual' && <span className="text-[9px] text-slate-600 uppercase hidden md:inline">{p.source.replace('apify_linkedin_', '')}</span>}
                  <span className="font-mono text-xs text-slate-500">{p.score}</span>
                  {p.requires_review && <Mail className="w-3 h-3 text-amber-400" />}
                  <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[p.status] || 'text-slate-400 border-slate-600'}`}>{p.status}</Badge>
                  <ChevronRight className="w-4 h-4 text-slate-600" />
                </div>
              </button>
            ))
          )}
        </div>

        {/* Create Dialog */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="bg-slate-900 border-slate-700 text-slate-200 max-w-md">
            <DialogHeader><DialogTitle className="text-white">New Prospect</DialogTitle></DialogHeader>
            <div className="grid gap-3">
              <div><Label className="text-slate-400 text-xs">Company Name *</Label><Input className="bg-slate-800 border-slate-700 text-slate-200 h-8 rounded-sm mt-1" value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} data-testid="prospect-company-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-slate-400 text-xs">Contact Name</Label><Input className="bg-slate-800 border-slate-700 text-slate-200 h-8 rounded-sm mt-1" value={form.contact_name} onChange={e => setForm({...form, contact_name: e.target.value})} /></div>
                <div><Label className="text-slate-400 text-xs">Role</Label><Input className="bg-slate-800 border-slate-700 text-slate-200 h-8 rounded-sm mt-1" value={form.contact_role} onChange={e => setForm({...form, contact_role: e.target.value})} /></div>
              </div>
              <div><Label className="text-slate-400 text-xs">Email</Label><Input type="email" className="bg-slate-800 border-slate-700 text-slate-200 h-8 rounded-sm mt-1" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="prospect-email-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-slate-400 text-xs">Phone</Label><Input className="bg-slate-800 border-slate-700 text-slate-200 h-8 rounded-sm mt-1" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} /></div>
                <div><Label className="text-slate-400 text-xs">Region</Label><Input className="bg-slate-800 border-slate-700 text-slate-200 h-8 rounded-sm mt-1" value={form.region} onChange={e => setForm({...form, region: e.target.value})} /></div>
              </div>
              <div><Label className="text-slate-400 text-xs">Tags (comma-separated)</Label><Input className="bg-slate-800 border-slate-700 text-slate-200 h-8 rounded-sm mt-1" value={form.tags} onChange={e => setForm({...form, tags: e.target.value})} /></div>
              <div><Label className="text-slate-400 text-xs">Notes</Label><Textarea className="bg-slate-800 border-slate-700 text-slate-200 rounded-sm mt-1 min-h-[60px]" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>
            </div>
            <DialogFooter>
              <Button variant="ghost" className="text-slate-400" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm" onClick={handleCreate} disabled={!form.company_name} data-testid="save-prospect-btn">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detail Dialog */}
        <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
          <DialogContent className="bg-slate-900 border-slate-700 text-slate-200 max-w-lg">
            {selected && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-white flex items-center gap-2">
                    {selected.company_name}
                    {selected.contact_confidence && (
                      <span className="inline-flex items-center gap-1">
                        {CONFIDENCE_ICONS[selected.contact_confidence]}
                        <span className="text-[10px] text-slate-500">{selected.contact_confidence}</span>
                      </span>
                    )}
                  </DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <div><span className="text-slate-500">Contact:</span> <span className="text-slate-200">{selected.contact_name || '—'}</span></div>
                  <div><span className="text-slate-500">Role:</span> <span className="text-slate-200">{selected.contact_role || '—'}</span></div>
                  <div><span className="text-slate-500">Email:</span> <span className="text-slate-200">{selected.email || '—'}</span></div>
                  <div><span className="text-slate-500">Phone:</span> <span className="text-slate-200">{selected.phone || '—'}</span></div>
                  <div><span className="text-slate-500">Region:</span> <span className="text-slate-200">{selected.region || '—'}</span></div>
                  <div><span className="text-slate-500">Source:</span> <span className="text-slate-200">{selected.source}</span></div>
                  <div><span className="text-slate-500">Status:</span> <Badge variant="outline" className={`ml-1 text-[10px] ${STATUS_COLORS[selected.status] || ''}`}>{selected.status}</Badge></div>
                  <div><span className="text-slate-500">Score:</span> <span className="font-mono text-orange-400">{selected.score}</span>{selected.grade && <span className="text-[10px] text-slate-500 ml-1">({selected.grade})</span>}</div>
                </div>
                {/* Pipeline fields */}
                {(selected.contact_type || selected.sendable || selected.search_category) && (
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm mt-2 pt-2 border-t border-slate-800">
                    {selected.contact_type && <div><span className="text-slate-500">Contact Type:</span> <span className="text-slate-200">{selected.contact_type}</span></div>}
                    {selected.sendable && <div><span className="text-slate-500">Sendable:</span> <span className={selected.sendable === 'true' ? 'text-emerald-400' : selected.sendable === 'review' ? 'text-amber-400' : 'text-red-400'}>{selected.sendable}</span></div>}
                    {selected.search_category && <div><span className="text-slate-500">Category:</span> <span className="text-slate-200">{selected.search_category}</span></div>}
                    {selected.requires_review && <div><span className="text-amber-400 text-xs">Requires Review</span></div>}
                  </div>
                )}
                {selected.tags?.length > 0 && (
                  <div className="flex gap-1 flex-wrap mt-1">
                    {selected.tags.map(t => <Badge key={t} variant="outline" className="text-[10px] text-slate-400 border-slate-600">{t}</Badge>)}
                  </div>
                )}
                {selected.notes && <p className="text-xs text-slate-400 mt-2 bg-slate-800 p-2 rounded-sm">{selected.notes}</p>}
                <div className="mt-3 border-t border-slate-800 pt-3">
                  <h3 className="text-xs text-slate-500 uppercase tracking-wider mb-2">Activity Log</h3>
                  {activities.length === 0 ? (
                    <p className="text-xs text-slate-600">No activity yet</p>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {activities.map(a => (
                        <div key={a.id} className="flex items-start gap-2 text-xs">
                          <span className="text-slate-600 font-mono whitespace-nowrap">{new Date(a.created_at).toLocaleDateString()}</span>
                          <Badge variant="outline" className="text-[9px] text-slate-400 border-slate-700">{a.type}</Badge>
                          <span className="text-slate-400 truncate">{a.subject || a.outcome}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
            {selected && (
              <div className="flex justify-end pt-2 border-t border-slate-800">
                <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300 hover:bg-red-500/10" onClick={() => handleDelete(selected.id)} data-testid="delete-prospect-btn">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </BackofficeLayout>
  );
}
